// SEO-friendly URL generation for location-based car rentals
import { getLocationBySlug, findLocationByCity, slugToCityName } from '../services/dynamicLocationsService'

export interface LocationSeoData {
  slug: string;
  name: string;
  arabicName?: string;
}

// Location slug mappings for different languages
export const locationSlugs: Record<string, LocationSeoData> = {
  'casablanca': {
    slug: 'casablanca',
    name: 'Casablanca',
    arabicName: 'الدار البيضاء'
  },
  'rabat': {
    slug: 'rabat',
    name: 'Rabat',
    arabicName: 'الرباط'
  },
  'marrakech': {
    slug: 'marrakech',
    name: 'Marrakech',
    arabicName: 'مراكش'
  },
  'fes': {
    slug: 'fes',
    name: 'Fès',
    arabicName: 'فاس'
  },
  'tanger': {
    slug: 'tanger',
    name: 'Tanger',
    arabicName: 'طنجة'
  },
  'agadir': {
    slug: 'agadir',
    name: 'Agadir',
    arabicName: 'أكادير'
  },
  'meknes': {
    slug: 'meknes',
    name: 'Meknès',
    arabicName: 'مكناس'
  },
  'oujda': {
    slug: 'oujda',
    name: 'Oujda',
    arabicName: 'وجدة'
  },
  'tetouan': {
    slug: 'tetouan',
    name: 'Tétouan',
    arabicName: 'تطوان'
  },
  'kenitra': {
    slug: 'kenitra',
    name: 'Kénitra',
    arabicName: 'القنيطرة'
  },
  'sale': {
    slug: 'sale',
    name: 'Salé',
    arabicName: 'سلا'
  },
  'mohammedia': {
    slug: 'mohammedia',
    name: 'Mohammedia',
    arabicName: 'المحمدية'
  },
  'el-jadida': {
    slug: 'el-jadida',
    name: 'El Jadida',
    arabicName: 'الجديدة'
  },
  'beni-mellal': {
    slug: 'beni-mellal',
    name: 'Beni Mellal',
    arabicName: 'بني ملال'
  },
  'nador': {
    slug: 'nador',
    name: 'Nador',
    arabicName: 'الناظور'
  },
  'khouribga': {
    slug: 'khouribga',
    name: 'Khouribga',
    arabicName: 'خريبكة'
  },
  'settat': {
    slug: 'settat',
    name: 'Settat',
    arabicName: 'سطات'
  },
  'taza': {
    slug: 'taza',
    name: 'Taza',
    arabicName: 'تازة'
  },
  'ouarzazate': {
    slug: 'ouarzazate',
    name: 'Ouarzazate',
    arabicName: 'ورزازات'
  },
  'errachidia': {
    slug: 'errachidia',
    name: 'Errachidia',
    arabicName: 'الراشيدية'
  },
  'ifran': {
    slug: 'ifran',
    name: 'Ifrane',
    arabicName: 'إفران'
  }
};

/**
 * Generate SEO-friendly URL path for location-based car rental
 */
export function generateLocationUrl(locationSlug: string, language: 'fr' | 'en' | 'ar'): string {
  // Use the provided slug directly (it should already be properly formatted)
  const cityName = locationSlug;
  
  switch (language) {
    case 'fr':
      return `/location-voiture/${cityName}`;
    case 'en':
      return `/rent-car/${cityName}`;
    case 'ar':
      // For Arabic, we'll use transliterated version for URL but Arabic in meta
      return `/تأجير-سيارة/${cityName}`;
    default:
      return `/location-voiture/${cityName}`;
  }
}

/**
 * Extract location slug from SEO URL
 */
export function extractLocationFromUrl(pathname: string): string | null {
  // Match French pattern: /location-voiture/{city}
  const frMatch = pathname.match(/\/location-voiture\/(.+)/);
  if (frMatch) {
    const locationSlug = decodeURIComponent(frMatch[1]);
    // Convert URL-encoded spaces to hyphens for consistency
    return locationSlug.replace(/\s+/g, '-');
  }
  
  // Match English pattern: /rent-car/{city}
  const enMatch = pathname.match(/\/rent-car\/(.+)/);
  if (enMatch) {
    const locationSlug = decodeURIComponent(enMatch[1]);
    // Convert URL-encoded spaces to hyphens for consistency
    return locationSlug.replace(/\s+/g, '-');
  }
  
  // Match Arabic pattern: /تأجير-سيارة/{city}
  const arMatch = pathname.match(/\/تأجير-سيارة\/(.+)/);
  if (arMatch) {
    const locationSlug = decodeURIComponent(arMatch[1]);
    // Convert URL-encoded spaces to hyphens for consistency
    return locationSlug.replace(/\s+/g, '-');
  }
  
  return null;
}

/**
 * Generate SEO-optimized title for location page
 */
export async function generateLocationTitle(locationSlug: string, language: 'fr' | 'en' | 'ar'): Promise<string> {
  const location = await getLocationBySlug(locationSlug);
  if (!location) {
    // Fallback: convert slug to city name
    const cityName = slugToCityName(locationSlug);
    switch (language) {
      case 'fr':
        return `Location de Voiture à ${cityName} | KiraCar`;
      case 'en':
        return `Car Rental in ${cityName} | KiraCar`;
      case 'ar':
        return `تأجير السيارات في ${cityName} | كيراكار`;
      default:
        return `Location de Voiture à ${cityName} | KiraCar`;
    }
  }

  const cityName = language === 'ar' && location.arabicName ? location.arabicName : location.name;
  
  switch (language) {
    case 'fr':
      return `Location de Voiture à ${cityName} | KiraCar`;
    case 'en':
      return `Car Rental in ${cityName} | KiraCar`;
    case 'ar':
      return `تأجير السيارات في ${cityName} | كيراكار`;
    default:
      return `Location de Voiture à ${cityName} | KiraCar`;
  }
}

/**
 * Generate SEO-optimized description for location page
 */
export function generateLocationDescription(locationSlug: string, language: 'fr' | 'en' | 'ar'): string {
  const location = locationSlugs[locationSlug];
  if (!location) {
    return language === 'fr' ? 'Louez une voiture au Maroc avec KiraCar' : 
           language === 'en' ? 'Rent a car in Morocco with KiraCar' : 
           'استأجر سيارة في المغرب مع كيراكار';
  }

  const cityName = language === 'ar' ? location.arabicName || location.name : location.name;
  
  switch (language) {
    case 'fr':
      return `Louez une voiture à ${cityName} avec KiraCar. Large sélection de véhicules, prix compétitifs et réservation en ligne facile. Service de qualité garanti.`;
    case 'en':
      return `Rent a car in ${cityName} with KiraCar. Wide selection of vehicles, competitive prices and easy online booking. Quality service guaranteed.`;
    case 'ar':
      return `استأجر سيارة في ${cityName} مع كيراكار. مجموعة واسعة من المركبات، أسعار تنافسية وحجز سهل عبر الإنترنت. خدمة عالية الجودة مضمونة.`;
    default:
      return `Louez une voiture à ${cityName} avec KiraCar. Large sélection de véhicules, prix compétitifs et réservation en ligne facile.`;
  }
}

/**
 * Generate SEO-friendly car detail URL with location
 */
export function generateCarDetailUrl(locationSlug: string, carSlug: string, language: 'fr' | 'en' | 'ar'): string {
  const location = locationSlugs[locationSlug];
  if (!location) {
    // Fallback to regular car detail URL if location not found
    return `/cars/${carSlug}`;
  }

  const cityName = location.slug;
  
  switch (language) {
    case 'fr':
      return `/location-voiture/${cityName}/${carSlug}`;
    case 'en':
      return `/rent-car/${cityName}/${carSlug}`;
    case 'ar':
      return `/تأجير-سيارة/${cityName}/${carSlug}`;
    default:
      return `/location-voiture/${cityName}/${carSlug}`;
  }
}

/**
 * Extract location and car slug from car detail URL
 */
export function extractCarDetailFromUrl(pathname: string): { locationSlug: string | null; carSlug: string | null } {
  // Match French pattern: /location-voiture/{city}/{car-slug}
  const frMatch = pathname.match(/\/location-voiture\/([^\/]+)\/(.+)/);
  if (frMatch) return { locationSlug: frMatch[1], carSlug: frMatch[2] };
  
  // Match English pattern: /rent-car/{city}/{car-slug}
  const enMatch = pathname.match(/\/rent-car\/([^\/]+)\/(.+)/);
  if (enMatch) return { locationSlug: enMatch[1], carSlug: enMatch[2] };
  
  // Match Arabic pattern: /تأجير-سيارة/{city}/{car-slug}
  const arMatch = pathname.match(/\/تأجير-سيارة\/([^\/]+)\/(.+)/);
  if (arMatch) return { locationSlug: arMatch[1], carSlug: arMatch[2] };
  
  return { locationSlug: null, carSlug: null };
}

/**
 * Generate SEO-optimized keywords for location page
 */
export function generateLocationKeywords(locationSlug: string, language: 'fr' | 'en' | 'ar'): string {
  const location = locationSlugs[locationSlug];
  if (!location) {
    return language === 'fr' ? 'location voiture maroc, rent car morocco' : 
           language === 'en' ? 'car rental morocco, rent car morocco' : 
           'تأجير سيارات المغرب, كراء السيارات المغرب';
  }

  const cityName = language === 'ar' ? location.arabicName || location.name : location.name;
  const citySlug = location.slug;
  
  switch (language) {
    case 'fr':
      return `location voiture ${citySlug}, location voiture ${cityName}, rent car ${citySlug}, voiture de location ${cityName}, kiracar ${cityName}, location auto ${cityName}`;
    case 'en':
      return `car rental ${citySlug}, rent car ${cityName}, car hire ${cityName}, vehicle rental ${cityName}, kiracar ${cityName}, auto rental ${cityName}`;
    case 'ar':
      return `تأجير سيارات ${cityName}, كراء السيارات ${cityName}, تأجير سيارة ${cityName}, كيراكار ${cityName}, استئجار سيارة ${cityName}`;
    default:
      return `location voiture ${citySlug}, rent car ${cityName}, kiracar ${cityName}`;
  }
}
